import React, { useState, useEffect } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, hasAnyRole } from "@/lib/api";
import { toast } from "sonner";
import { Languages, Palette, Bell, UserCircle, Building, Upload, Image as ImageIcon, X, MessageCircle, Plus, Trash2, Save, RotateCcw } from "lucide-react";

export default function SettingsPage() {
  const { lang, setLang } = useI18n();
  const { user } = useAuth();
  const { company, reload } = useCompany();
  const isAdmin = hasAnyRole(user, ["admin"]);
  const [companyForm, setCompanyForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (company) setCompanyForm(company); }, [company]);

  const onLogoFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Logo too large (max 2 MB)");
    const reader = new FileReader();
    reader.onload = () => setCompanyForm({ ...companyForm, logo_data_url: reader.result });
    reader.readAsDataURL(f);
  };

  const onQrFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("QR image too large (max 2 MB)");
    const reader = new FileReader();
    reader.onload = () => setCompanyForm({ ...companyForm, duitnow_qr_data_url: reader.result });
    reader.readAsDataURL(f);
  };

  const saveCompany = async () => {
    setSaving(true);
    try {
      await api.patch("/company-settings", companyForm);
      await reload();
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <div className="label-eyebrow">Preferences</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Settings</h1>
      </div>

      <div className="space-y-4">
        {/* Profile */}
        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-1">
            <UserCircle className="w-4 h-4 text-[#D4AF37]" />
            <div className="label-eyebrow">Profile</div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
            <Field label="Name" value={user?.name} />
            <Field label="Email" value={user?.email} keepCase />
            <Field label="Role" value={user?.role} />
            <Field label="Phone" value={user?.phone || "—"} keepCase />
          </div>
        </div>

        {/* Company branding — admin only */}
        {isAdmin && (
          <div className="card-lux p-6" data-testid="company-card">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-4 h-4 text-[#D4AF37]" />
              <div className="label-eyebrow">Company Branding</div>
            </div>

            {/* Logo upload */}
            <div className="flex items-center gap-4 mb-4 p-4 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]">
              <div className="w-20 h-20 rounded-sm overflow-hidden flex items-center justify-center shrink-0" style={{ background: companyForm.logo_data_url ? "#fff" : "linear-gradient(135deg,#D4AF37,#8A7322)" }}>
                {companyForm.logo_data_url ? (
                  <img src={companyForm.logo_data_url} alt="logo" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-black" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm text-zinc-200 mb-2">Logo (PNG/JPG, max 2 MB)</div>
                <div className="flex gap-2">
                  <label className="px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer text-xs flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" /> Upload
                    <input type="file" accept="image/*" onChange={onLogoFile} data-testid="logo-input" className="hidden" />
                  </label>
                  {companyForm.logo_data_url && (
                    <button onClick={() => setCompanyForm({ ...companyForm, logo_data_url: null })} className="px-3 py-2 rounded-sm border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs flex items-center gap-1.5" data-testid="logo-remove">
                      <X className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Company name">
                <Input value={companyForm.company_name || ""} onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })} data-testid="company-name" className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Phone">
                <Input value={companyForm.phone || ""} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Email">
                <Input value={companyForm.email || ""} onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Website">
                <Input value={companyForm.website || ""} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Instagram URL">
                <Input value={companyForm.instagram_url || ""} onChange={(e) => setCompanyForm({ ...companyForm, instagram_url: e.target.value })} placeholder="https://instagram.com/hnccatering" data-testid="company-instagram" className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Facebook URL">
                <Input value={companyForm.facebook_url || ""} onChange={(e) => setCompanyForm({ ...companyForm, facebook_url: e.target.value })} placeholder="https://facebook.com/hnccatering" data-testid="company-facebook" className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Instagram posts to feature (up to 3 links, one per line)" wide>
                <textarea
                  value={(companyForm.instagram_post_urls || []).join("\n")}
                  onChange={(e) => setCompanyForm({ ...companyForm, instagram_post_urls: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 3) })}
                  rows={3}
                  placeholder={"https://www.instagram.com/p/XXXXXXXX/\nhttps://www.instagram.com/reel/YYYYYYYY/"}
                  data-testid="company-ig-posts"
                  className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm p-2 text-sm"
                />
                <p className="text-[10px] text-zinc-500 mt-1">These posts appear live on the customer tracking page & portal, next to your live Facebook Page preview.</p>
              </FormField>
              <FormField label="Address" wide>
                <Input value={companyForm.address || ""} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Payment instructions" wide>
                <Input value={companyForm.payment_instructions || ""} onChange={(e) => setCompanyForm({ ...companyForm, payment_instructions: e.target.value })} className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Invoice number prefix">
                <Input placeholder="HNC" value={companyForm.invoice_prefix || ""} onChange={(e) => setCompanyForm({ ...companyForm, invoice_prefix: e.target.value })} data-testid="invoice-prefix-input" className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
              <FormField label="Next invoice number">
                <Input type="number" min="1" placeholder="e.g. 3332614" value={companyForm.invoice_next_number ?? ""} onChange={(e) => setCompanyForm({ ...companyForm, invoice_next_number: e.target.value === "" ? null : Number(e.target.value) })} data-testid="invoice-next-number-input" className="bg-[#050505] border-[#2a2a2a]" />
                <p className="text-[10px] text-zinc-600 mt-1">Set your starting number once — each new invoice auto-increments from here.</p>
              </FormField>
              <FormField label="DuitNow QR (shown on invoices)" wide>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-sm overflow-hidden bg-white flex items-center justify-center shrink-0 border border-[#2a2a2a]">
                    {companyForm.duitnow_qr_data_url ? (
                      <img src={companyForm.duitnow_qr_data_url} alt="DuitNow QR" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-zinc-400" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <label className="px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer text-xs flex items-center gap-1.5">
                      <Upload className="w-3.5 h-3.5" /> Upload QR
                      <input type="file" accept="image/*" onChange={onQrFile} data-testid="duitnow-qr-input" className="hidden" />
                    </label>
                    {companyForm.duitnow_qr_data_url && (
                      <button onClick={() => setCompanyForm({ ...companyForm, duitnow_qr_data_url: null })} className="px-3 py-2 rounded-sm border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs flex items-center gap-1.5" data-testid="duitnow-qr-remove">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </FormField>
              <FormField label="Tax rate (%)">
                <Input type="number" step="0.1" value={companyForm.tax_rate ?? 0} onChange={(e) => setCompanyForm({ ...companyForm, tax_rate: Number(e.target.value) })} className="bg-[#050505] border-[#2a2a2a]" />
              </FormField>
            </div>

            <Button onClick={saveCompany} disabled={saving} data-testid="company-save" className="mt-4 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
              {saving ? "Saving…" : "Save branding"}
            </Button>
          </div>
        )}

        {/* WhatsApp templates (admin only) */}
        {isAdmin && <WaTemplatesCard />}

        {/* Enquiry services (admin only) */}
        {isAdmin && <ServicesCard />}

        {/* Language */}
        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-3">
            <Languages className="w-4 h-4 text-[#D4AF37]" />
            <div className="label-eyebrow">Language</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setLang("en")} data-testid="lang-en"
              className={`rounded-sm ${lang === "en" ? "bg-[#D4AF37] text-black hover:bg-[#E5C86B]" : "bg-transparent border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>English</Button>
            <Button onClick={() => setLang("zh")} data-testid="lang-zh"
              className={`rounded-sm ${lang === "zh" ? "bg-[#D4AF37] text-black hover:bg-[#E5C86B]" : "bg-transparent border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>中文</Button>
          </div>
        </div>

        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-[#D4AF37]" />
            <div className="label-eyebrow">Theme</div>
          </div>
          <p className="text-sm text-zinc-500">HNC is crafted in dark luxury mode by default — a stage befitting your finest moments.</p>
        </div>

        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-4 h-4 text-[#D4AF37]" />
            <div className="label-eyebrow">Notifications</div>
          </div>
          <p className="text-sm text-zinc-500 mt-1">WhatsApp click-to-send is enabled on every order. Browser push notifications coming soon.</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, keepCase }) {
  return (
    <div>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`text-sm mt-1 ${keepCase ? "" : "capitalize"}`}>{value}</div>
    </div>
  );
}

function ServicesCard() {
  const [services, setServices] = useState([]);
  const [newName, setNewName] = useState("");

  const load = async () => {
    try { const r = await api.get("/services"); setServices(r.data || []); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    await api.post("/services", { name: newName.trim(), active: true });
    setNewName(""); toast.success("Service added"); load();
  };
  const toggle = async (s) => {
    await api.patch(`/services/${s.id}`, { name: s.name, active: !s.active });
    toast.success(!s.active ? `"${s.name}" is now visible on the enquiry form` : `"${s.name}" hidden from the enquiry form`);
    load();
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.name}"?`)) return;
    await api.delete(`/services/${s.id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="card-lux p-6" data-testid="services-card">
      <div className="flex items-center gap-2 mb-1">
        <Building className="w-4 h-4 text-[#D4AF37]" />
        <div className="label-eyebrow">Enquiry Services</div>
      </div>
      <p className="text-sm text-zinc-500 mt-1 mb-4">Services customers can request on the Plan Your Event form.</p>
      <div className="space-y-2 mb-4">
        {services.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-sm bg-[#050505] border border-[#1a1a1a]" data-testid={`service-row-${s.id}`}>
            <span className={`flex-1 text-sm ${s.active ? "text-zinc-200" : "text-zinc-600 line-through"}`}>{s.name}</span>
            <span className={`text-[10px] uppercase tracking-wider ${s.active ? "text-emerald-400" : "text-zinc-600"}`}>{s.active ? "Active" : "Inactive"}</span>
            <Switch checked={!!s.active} onCheckedChange={() => toggle(s)} data-testid={`service-toggle-${s.id}`} className="data-[state=checked]:bg-emerald-500" />
            <button onClick={() => remove(s)} data-testid={`service-delete-${s.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {services.length === 0 && <div className="text-xs text-zinc-600">No services yet.</div>}
      </div>
      <div className="flex gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a service…" data-testid="service-new-input" className="bg-[#050505] border-[#2a2a2a]" />
        <Button onClick={add} data-testid="service-add" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm"><Plus className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}

function FormField({ label, wide, children }) {
  return (
    <div className={`space-y-1.5 ${wide ? "md:col-span-2" : ""}`}>
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}

const STAGE_OPTIONS = [
  { value: "", label: "— Custom (not stage-linked) —" },
  { value: "order_confirmed", label: "Order confirmed" },
  { value: "kitchen_preparing", label: "Kitchen preparing" },
  { value: "driver_assigned", label: "Driver assigned" },
  { value: "on_the_way", label: "Driver on the way" },
  { value: "setup_in_progress", label: "Setup in progress" },
  { value: "event_ready", label: "Event ready" },
  { value: "completed", label: "Event completed" },
];

function WaTemplatesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template being edited / created
  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/wa-templates");
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async (t) => {
    try {
      if (t.id) {
        await api.patch(`/wa-templates/${t.id}`, {
          label_en: t.label_en, label_cn: t.label_cn,
          message_en: t.message_en, message_cn: t.message_cn,
          stage: t.stage || null, is_active: t.is_active !== false,
        });
        toast.success("Template updated");
      } else {
        await api.post("/wa-templates", {
          label_en: t.label_en, label_cn: t.label_cn || "",
          message_en: t.message_en, message_cn: t.message_cn || "",
          stage: t.stage || null,
          order_index: 999, is_active: true,
        });
        toast.success("Template created");
      }
      setEditing(null); await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.label_en}"?`)) return;
    try {
      await api.delete(`/wa-templates/${t.id}`);
      toast.success("Template deleted");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const resetDefaults = async () => {
    if (!window.confirm("Reset the 8 default templates back to factory wording? Your custom templates will be kept.")) return;
    try {
      await api.post("/wa-templates/reset-defaults");
      toast.success("Defaults restored");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="card-lux p-6" data-testid="wa-templates-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-400" />
          <div className="label-eyebrow">WhatsApp · Customer Notify Templates</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={resetDefaults} variant="outline" size="sm" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm h-7 text-xs" data-testid="wa-reset-defaults"><RotateCcw className="w-3 h-3 mr-1" />Reset defaults</Button>
          <Button onClick={() => setEditing({ label_en: "", label_cn: "", message_en: "", message_cn: "", stage: "" })} variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm h-7 text-xs" data-testid="wa-add-template"><Plus className="w-3 h-3 mr-1" />Add template</Button>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mt-1 mb-4">EN + 中文 versions per template. Use placeholders: <span className="text-[#D4AF37]/80 font-mono text-[10px]">{`{customer_name} {job_title} {package_name} {event_date} {pax} {tracking_url} {tracking_code} {balance} {total}`}</span> — {`{package_name}`} auto-uses the order's Job Title when set.</p>
      {loading ? <div className="text-xs text-zinc-500">Loading…</div> : (
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]" data-testid={`wa-tpl-row-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-zinc-200">{t.label_en}</span>
                  {t.label_cn && <span className="text-xs text-zinc-500">· {t.label_cn}</span>}
                  {t.stage ? (
                    <span className="text-[9px] uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-sm px-1.5 py-0.5">stage · {t.stage.replace(/_/g, " ")}</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-zinc-700 rounded-sm px-1.5 py-0.5">custom</span>
                  )}
                  {t.is_default && <span className="text-[9px] uppercase tracking-widest text-amber-400/70">default</span>}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 truncate" title={t.message_en}>{t.message_en}</div>
              </div>
              <div className="flex gap-1">
                <Button onClick={() => setEditing(t)} variant="outline" size="sm" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm h-7 text-xs" data-testid={`wa-edit-${t.id}`}>Edit</Button>
                <Button onClick={() => remove(t)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-sm h-7 text-xs" data-testid={`wa-delete-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <WaTemplateEditor template={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function WaTemplateEditor({ template, onClose, onSave }) {
  const [f, setF] = useState({
    id: template.id,
    label_en: template.label_en || "",
    label_cn: template.label_cn || "",
    message_en: template.message_en || "",
    message_cn: template.message_cn || "",
    stage: template.stage || "",
    is_active: template.is_active !== false,
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="wa-template-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">{f.id ? "Edit Template" : "New Template"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <FormField label="Label · English">
              <Input value={f.label_en} onChange={(e) => setF({ ...f, label_en: e.target.value })} data-testid="wa-tpl-label-en" placeholder="e.g. Order confirmed" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label="Label · 中文">
              <Input value={f.label_cn} onChange={(e) => setF({ ...f, label_cn: e.target.value })} data-testid="wa-tpl-label-cn" placeholder="例如:订单已确认" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
          </div>
          <FormField label="Workflow Stage (optional)">
            <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} data-testid="wa-tpl-stage" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              {STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FormField>
          <FormField label="Message · English">
            <textarea value={f.message_en} onChange={(e) => setF({ ...f, message_en: e.target.value })} data-testid="wa-tpl-msg-en" rows={4} placeholder="Hi {customer_name}, ..." className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 py-2 text-sm font-mono leading-relaxed" />
          </FormField>
          <FormField label="Message · 中文">
            <textarea value={f.message_cn} onChange={(e) => setF({ ...f, message_cn: e.target.value })} data-testid="wa-tpl-msg-cn" rows={4} placeholder="您好 {customer_name},..." className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 py-2 text-sm font-mono leading-relaxed" />
          </FormField>
          <p className="text-[10px] text-zinc-500">Placeholders will be filled with the actual order values when the button is clicked.</p>
          <div className="flex gap-2 justify-end pt-2">
            <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm" data-testid="wa-tpl-cancel">Cancel</Button>
            <Button onClick={() => onSave(f)} disabled={!f.label_en || !f.message_en} className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm" data-testid="wa-tpl-save"><Save className="w-3.5 h-3.5 mr-1.5" />{f.id ? "Save Changes" : "Create Template"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
